import AppBar from '@material-ui/core/AppBar';
import { Button, SolvTheme, Stack } from '@solvhealth/jigsaw-v1';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import isEmpty from 'lodash/isEmpty';
import LocationInterface from '@solvhealth/types/interfaces/Location';
import history from '../../../../../core/history';
import { safeGet } from '../../../../../core/util/object';
import useSetTitle from '../../../../../hooks/useSetTitle';
import LoadingScreen from '../../../../SolvPatternLibrary/LoadingScreen';
import PaperworkLayout from '../../../PaperworkLayout';
import { useIncrementProgress, useInitializePaperworkData } from '~/components/Paperwork/hooks';
import FinalizeCard from '~/components/Paperwork/components/Finalize/FinalizeCard';
import { PGSectionNumber, PG_SECTIONS } from '../../constants';
import FinalizePhotoIdCard from '~/components/Paperwork/components/Finalize/FinalizePhotoIdCard';
import { PaperworkState } from '~/ducks/paperwork';
import { calculateProgress } from '../../../util';
import useGetContentForSection from '../../../components/Finalize/hooks/useGetContentForSection';
import usePushToPaperworkComplete from '../../../components/Finalize/hooks/usePushToPaperworkComplete';
import useConsentSectionContent from '../../../components/Finalize/hooks/useConsentSectionContent';
import { SectionPage } from '../../../interfaces';
import { getFieldsWithDependenciesSatisified } from '../../util/fieldDependencies';

const BottomBar = styled(AppBar).withConfig({ componentId: 'sc-qa1v1k-0' })`
  top: auto;
  bottom: 0;
  display: flex;
  flex-flow: row;
  flex-wrap: nowrap;
  justify-content: space-around;
  background-color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
`;

const Spacer = styled.div.withConfig({ componentId: 'sc-qa1v1k-1' })`
  height: 100px;
  background: white;
  width: 100%;
`;

type Props = {
  bookingId: string;
  location: LocationInterface;
  paperwork: PaperworkState;
};

const getSectionPagesFieldNames = (sectionNumber: PGSectionNumber) => {
  const sectionPages = PG_SECTIONS.get(sectionNumber)?.pages ?? new Map();
  return [...sectionPages.values()].reduce(
    (allFieldNames: string[], page: SectionPage) => allFieldNames.concat(page.fieldNames),
    []
  );
};

const PaperworkFinalize = (props: Props) => {
  const { bookingId, location, paperwork } = props;

  const { t } = useTranslation(['paperwork', 'providerGroupPaperwork']);
  const title = t('paperwork:finalize.htmlTitle');

  useSetTitle(title);
  useIncrementProgress(PGSectionNumber.Finalize, paperwork.pageView);
  useInitializePaperworkData(paperwork, bookingId, location);

  const getContentForSection = useGetContentForSection('providerGroupPaperwork');
  const consentSectionContent = useConsentSectionContent(bookingId);
  const pushToPaperworkComplete = usePushToPaperworkComplete(bookingId);

  const goBackAPage = () => {
    history.goBack();
  };

  if (!safeGet(paperwork)('standardFields')) {
    return <LoadingScreen />;
  }

  const patientSectionFieldNames = getFieldsWithDependenciesSatisified(
    getSectionPagesFieldNames(PGSectionNumber.Patient),
    paperwork
  );
  const medicalSectionFieldNames = getFieldsWithDependenciesSatisified(
    getSectionPagesFieldNames(PGSectionNumber.Medical),
    paperwork
  );
  const lifestyleFactorsSectionFieldNames = getFieldsWithDependenciesSatisified(
    getSectionPagesFieldNames(PGSectionNumber.LifestyleFactors),
    paperwork
  );
  const healthcareContactsSectionFieldNames = getFieldsWithDependenciesSatisified(
    getSectionPagesFieldNames(PGSectionNumber.HealthcareContacts),
    paperwork
  );
  const verificationAndReviewSectionFieldNames = getFieldsWithDependenciesSatisified(
    getSectionPagesFieldNames(PGSectionNumber.VerificationAndReview),
    paperwork
  );

  const patientTitle = t(`providerGroupPaperwork:${PGSectionNumber.Patient}.sectionTitle` as any);

  const medicalTitle = t(`providerGroupPaperwork:${PGSectionNumber.Medical}.sectionTitle` as any);

  const lifestyleFactorsTitle = t(
    `providerGroupPaperwork:${PGSectionNumber.LifestyleFactors}.sectionTitle` as any
  );

  const healthcareContactsTitle = t(
    `providerGroupPaperwork:${PGSectionNumber.HealthcareContacts}.sectionTitle` as any
  );
  const verificationAndReviewTitle = t(
    `providerGroupPaperwork:${PGSectionNumber.VerificationAndReview}.sectionTitle` as any
  );

  return (
    <PaperworkLayout
      noPadding
      onGoBack={goBackAPage}
      postTableContent={
        <>
          <Spacer />
          <BottomBar>
            <Stack flex="1" maxWidth={SolvTheme.breakpoints.sm} mx="auto" pb={3} pt={1} px={2}>
              <Button flex="1" fullWidth onClick={pushToPaperworkComplete}>
                {t('paperwork:finalize.submit')}
              </Button>
            </Stack>
          </BottomBar>
        </>
      }
      section={PGSectionNumber.VerificationAndReview}
      subtitle={t('paperwork:finalize.subtitle')}
      table
      title={t('paperwork:finalize.title')}
    >
      <FinalizeCard
        allFields={getContentForSection(patientSectionFieldNames)}
        sectionTitle={patientTitle}
        showEditButton={false}
      />
      <FinalizeCard
        allFields={getContentForSection(medicalSectionFieldNames)}
        sectionTitle={medicalTitle}
        showEditButton={false}
      />
      <FinalizeCard
        allFields={getContentForSection(lifestyleFactorsSectionFieldNames)}
        sectionTitle={lifestyleFactorsTitle}
        showEditButton={false}
      />
      <FinalizeCard
        allFields={getContentForSection(healthcareContactsSectionFieldNames)}
        sectionTitle={healthcareContactsTitle}
        showEditButton={false}
      />
      <FinalizePhotoIdCard showEditButton={false} />
      <FinalizeCard
        allFields={getContentForSection(verificationAndReviewSectionFieldNames)}
        sectionTitle={verificationAndReviewTitle}
        showEditButton={false}
      />
      {!isEmpty(paperwork?.consentForms?.forms) && paperwork.signatures?.success && (
        <FinalizeCard
          allFields={consentSectionContent}
          sectionTitle={t('paperwork:common.consentForms')}
          showEditButton={false}
        />
      )}
    </PaperworkLayout>
  );
};

export default PaperworkFinalize;
